import type { SessionUser } from './api/client'

/**
 * LE registre de navigation — la seule liste des écrans de la barre latérale.
 *
 * ─── Pourquoi un registre, et pas la liste écrite dans le composant ───
 *
 * `AppLayout` ne doit connaître AUCUN écran en particulier. Sans ce fichier, la
 * barre latérale devient l'endroit où l'on écrit « si l'utilisateur est admin,
 * afficher Organisation », puis « sauf sur cet écran », puis « mais pas pour un
 * viewer » — et le composant de chrome finit par porter les règles d'accès de
 * toute l'application, à l'endroit où personne ne les cherche.
 *
 * Ici, ajouter un écran = une vue, une route, UNE ligne ici. Le supprimer =
 * retirer les trois. C'est le même principe que `domain/registration.ts` côté
 * backend, appliqué à l'interface.
 *
 * Ce que ce registre ne contient PAS : « Mon profil » et « Paramètres de
 * l'organisation ». Ce sont des panneaux du menu du compte, pas des écrans — la
 * barre latérale liste des lieux de travail, on n'y « va » pas régler son thème.
 *
 * ─── Les deux périmètres ───
 *
 * `utilisateur` — le métier de l'application, ce que voit un client.
 * `superuser`   — l'exploitation de la PLATEFORME, réservée à l'organisation de
 *                 gestion (`organisations.is_hyperadmin`, migration 0006).
 *
 * Un seul jeu d'items est affiché à la fois : mélanger les deux dans une même
 * liste, c'est offrir à l'exploitant un menu où l'écran d'un client et l'annuaire
 * de tous les clients se ressemblent.
 */

export type Portee = 'utilisateur' | 'superuser'
export type RoleUtilisateur = SessionUser['role']

export interface EntreeNavigation {
  /** Nom de route Vue Router. C'est la clé : pas de chemin en dur ici. */
  nom: string
  /** Groupe d'affichage. Sert de clé i18n : `nav.groupe.<groupe>`. */
  groupe: string
  portee: Portee
  /**
   * Rôles autorisés. `undefined` = tous les rôles authentifiés.
   *
   * Ce filtrage est un CONFORT D'AFFICHAGE, pas une sécurité : chaque route et
   * chaque requête sont contrôlées côté serveur. Une interface qui cache un
   * bouton n'a jamais protégé une donnée — mais montrer un écran qui répondra
   * 403 est une mauvaise expérience, et fait remonter des tickets.
   */
  roles?: readonly RoleUtilisateur[]
  /**
   * Réservé à l'organisation de GESTION, sans passer par le périmètre
   * plateforme.
   *
   * Le droit et la CATÉGORIE sont deux questions distinctes, et les confondre
   * range mal les écrans. L'annuaire des identités n'est pas un réglage de la
   * plateforme : c'est le métier même du produit, et il vit donc dans le menu
   * de travail. Mais les identités n'ont pas d'`organisation_id` (ADR-001) :
   * la question « lesquelles appartiennent à cette organisation ? » n'a pas de
   * réponse, et seule l'organisation de gestion peut les consulter.
   *
   * D'où ce critère : un écran de travail, visible par les seuls comptes que
   * le serveur servira. Le périmètre `superuser`, lui, reste ce qu'il est —
   * les réglages de la plateforme (clients, offres, services).
   */
  exigeHyperadmin?: boolean
  /**
   * Épinglé au PIED du rail, hors des groupes.
   *
   * Le pied est la zone des recours : ce qu'on cherche quand le reste n'a pas
   * suffi, et qu'on ne veut pas voir défiler avec les lieux de travail. Un
   * drapeau du registre plutôt qu'un `if (entree.nom === 'developpeurs')` dans
   * le composant de chrome : la barre latérale ne doit connaître AUCUN écran en
   * particulier, sinon elle redevient l'endroit où l'on écrit les exceptions.
   */
  pied?: boolean
  /**
   * Tracés du pictogramme, SANS l'enveloppe `<svg>` (posée par le composant).
   *
   * Constantes littérales : le `v-html` du composant ne voit jamais de donnée
   * extérieure. Un pictogramme venu d'une API serait une injection HTML.
   */
  icone: string
}

export const NAVIGATION: readonly EntreeNavigation[] = [
  // L'écran Portail existe toujours — route « / », atterrissage après
  // connexion et consommation du `?lancement=` — mais il n'est PLUS une entrée
  // du rail : la section « Général » est retirée pour l'instant (2026-09-16).
  // La section réapparaît d'elle-même dès qu'une entrée de corps la cite à
  // nouveau ; « Développeurs », épinglée au pied, n'alimente pas les sections.
  {
    // AU PIED du rail, JUSTE AU-DESSUS de « Développeurs » : les comptes de
    // fournisseur sont un réglage qu'on pose une fois et qu'on rouvre
    // rarement — pas un lieu de travail quotidien. L'ordre de ce tableau est
    // celui de l'affichage.
    nom: 'comptes',
    groupe: 'general',
    portee: 'utilisateur',
    pied: true,
    icone:
      '<rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /><path d="M6 15h4" />',
  },
  {
    // AU PIED du rail : la documentation est un recours, là où on la cherche
    // quand le reste n'a pas suffi — pas un lieu de travail qu'on visite tous
    // les jours. Aucun filtre de rôles : documenter ne protège rien, et un
    // viewer a autant besoin de l'API qu'un admin.
    nom: 'developpeurs',
    groupe: 'general',
    portee: 'utilisateur',
    pied: true,
    icone: '<path d="m16 18 6-6-6-6" /><path d="m8 6-6 6 6 6" />',
  },

  // ---- ADMINISTRATION de la plateforme d'identité --------------------------
  // Le métier du produit, pas ses réglages : c'est ici qu'on travaille tous les
  // jours. Le groupe accueillera les applications et leurs rattachements.
  {
    nom: 'identites',
    groupe: 'administration',
    portee: 'utilisateur',
    exigeHyperadmin: true,
    icone: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />',
  },

  {
    // Avant les applications, et c'est l'ordre de lecture du produit : on
    // inscrit ce qu'on sait déployer, puis on dit où ça tourne.
    nom: 'logiciels',
    groupe: 'administration',
    portee: 'utilisateur',
    // Le catalogue est CLOISONNÉ par organisation (0119) : chaque parc a le
    // sien. Ce drapeau ne dit donc pas « donnée sans locataire » — il dit que
    // l'écran s'adresse à l'EXPLOITANT du parc, comme celui des applications.
    // La garde qui compte est côté serveur : la capacité `applications.gerer`,
    // que le menu ne sait pas lire et ne doit pas deviner.
    exigeHyperadmin: true,
    icone:
      '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" />',
  },
  {
    nom: 'applications',
    groupe: 'administration',
    portee: 'utilisateur',
    exigeHyperadmin: true,
    icone: '<rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><circle cx="17.5" cy="17.5" r="3.5" /><path d="M17.5 15.5v4" /><path d="M15.5 17.5h4" />',
  },

  // ---- Périmètre PLATEFORME ------------------------------------------------
  {
    nom: 'superuser.organisations',
    groupe: 'plateforme',
    portee: 'superuser',
    icone: '<path d="M3 21h18" /><path d="M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16" /><path d="M15 21V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v12" /><line x1="8" y1="8" x2="10" y2="8" /><line x1="8" y1="12" x2="10" y2="12" /><line x1="8" y1="16" x2="10" y2="16" />',
  },
  {
    nom: 'superuser.plans',
    groupe: 'plateforme',
    portee: 'superuser',
    icone: '<path d="M20.59 13.41 11 3H4v7l9.59 9.59a2 2 0 0 0 2.82 0l4.18-4.18a2 2 0 0 0 0-2.82z" /><circle cx="7.5" cy="7.5" r="1.5" />',
  },
  {
    nom: 'superuser.services',
    groupe: 'plateforme',
    portee: 'superuser',
    icone: '<rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" />',
  },
]

/**
 * L'organisation de l'utilisateur est-elle celle de gestion, et son rôle
 * suffisant ?
 *
 * MIROIR de `canUseSuperuser` (backend `core/org/rules.ts`). Le serveur reste
 * l'autorité : cette fonction ne sert qu'à décider d'AFFICHER la bascule et à
 * éviter d'ouvrir un écran qui répondrait 403. Toute modification de la règle se
 * répercute des deux côtés — sinon l'un cache ce que l'autre autorise
 * (frustration), ou l'inverse (l'utilisateur découvre un écran en erreur).
 */
export function peutUtiliserSuperuser(utilisateur: SessionUser | null): boolean {
  if (utilisateur === null) return false
  if (!utilisateur.isHyperadmin) return false
  return utilisateur.role === 'owner' || utilisateur.role === 'admin'
}

/** Entrées visibles pour un utilisateur, dans un périmètre donné. */
export function entreesVisibles(
  utilisateur: SessionUser | null,
  portee: Portee,
): EntreeNavigation[] {
  if (utilisateur === null) return []
  // Le périmètre plateforme n'existe pas pour qui n'y a pas droit : le demander
  // explicitement ne doit pas suffire à en obtenir les items.
  if (portee === 'superuser' && !peutUtiliserSuperuser(utilisateur)) return []

  return NAVIGATION.filter(
    (entree) =>
      entree.portee === portee &&
      // Le pied a sa propre zone : le laisser ici l'afficherait DEUX fois.
      entree.pied !== true &&
      (entree.roles === undefined || entree.roles.includes(utilisateur.role)) &&
      // `peutUtiliserSuperuser` et non le seul `isHyperadmin` : le serveur
      // exige AUSSI un rôle owner/admin. Afficher l'entrée à un `member` de
      // l'organisation de gestion lui promettrait un écran qui répondra 403.
      (entree.exigeHyperadmin !== true || peutUtiliserSuperuser(utilisateur)),
  )
}

/**
 * Entrées épinglées au PIED du rail — hors groupes, hors défilement.
 *
 * Mêmes filtres d'accès que le corps : `entreesVisibles` et cette fonction
 * doivent refuser exactement la même chose, sinon le pied devient la porte
 * dérobée d'un écran caché ailleurs.
 */
export function entreesDuPied(
  utilisateur: SessionUser | null,
  portee: Portee,
): EntreeNavigation[] {
  if (utilisateur === null) return []
  if (portee === 'superuser' && !peutUtiliserSuperuser(utilisateur)) return []

  return NAVIGATION.filter(
    (entree) =>
      entree.portee === portee &&
      entree.pied === true &&
      (entree.roles === undefined || entree.roles.includes(utilisateur.role)) &&
      (entree.exigeHyperadmin !== true || peutUtiliserSuperuser(utilisateur)),
  )
}

export interface GroupeNavigation {
  nom: string
  entrees: EntreeNavigation[]
}

/**
 * Entrées visibles, regroupées, dans l'ordre de DÉCLARATION du registre.
 *
 * L'ordre vient du fichier et non d'un tri alphabétique : « Tableau de bord »
 * avant « Éléments » est un choix éditorial, qu'un tri par nom casserait à la
 * première traduction.
 */
export function groupesVisibles(
  utilisateur: SessionUser | null,
  portee: Portee,
): GroupeNavigation[] {
  const groupes: GroupeNavigation[] = []
  for (const entree of entreesVisibles(utilisateur, portee)) {
    let groupe = groupes.find((g) => g.nom === entree.groupe)
    if (groupe === undefined) {
      groupe = { nom: entree.groupe, entrees: [] }
      groupes.push(groupe)
    }
    groupe.entrees.push(entree)
  }
  return groupes
}

/**
 * Premier écran atteignable d'un périmètre — sa page d'atterrissage.
 *
 * Utilisé au changement de périmètre : rester sur l'écran précédent laisserait
 * une barre latérale sans item actif, et l'utilisateur devant un contenu qui
 * n'appartient plus au menu affiché.
 */
export function accueilDuPerimetre(
  utilisateur: SessionUser | null,
  portee: Portee,
): string | null {
  // Les entrées de PIED ne sont pas des pages d'atterrissage : on n'ouvre pas
  // l'application sur sa documentation. Sans ce filtre, « Développeurs » —
  // premier du registre et épinglé au pied — devenait l'accueil.
  return entreesVisibles(utilisateur, portee).find((e) => e.pied !== true)?.nom ?? null
}

/** Périmètre auquel appartient une route, `null` si elle n'est pas dans la nav. */
export function porteeDeLaRoute(nomRoute: string | null | undefined): Portee | null {
  if (typeof nomRoute !== 'string') return null
  return NAVIGATION.find((entree) => entree.nom === nomRoute)?.portee ?? null
}
